/**
 * Validates selected variant products against live Shopify inventory before add-to-cart.
 * Covers multi-variant PDP, live single-product PDP, and quick order flows.
 */
(function () {
  var SKIP_FLAG = '__ttSkipVariantAvailabilityGuard';
  var refreshTimer = null;
  var oosGuardBlockedByContext = {
    'multi-variant': false,
    'quick-order': false
  };
  var OOS_UI_ITEM_CLASS = 'is-oos-error';
  var OOS_UI_AVAILABILITY_CLASS = 'is-oos';
  var oosUiAppliedVariantIds = {};
  var quickOrderOosUiAppliedVariantIds = {};
  var OOS_WARNING_ICON_HTML =
    '<span class="oos-warning-icon" aria-hidden="true">' +
    '<svg width="12" height="12" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M5.39952 0.177041C5.92257 -0.0590424 6.52195 -0.0589849 7.04502 0.177041C7.64522 0.448048 8.10223 1.26337 9.01475 2.89286L11.1944 6.78544C12.0716 8.35179 12.51 9.13534 12.4366 9.77665C12.3726 10.3361 12.0761 10.8429 11.6192 11.1722C11.0955 11.5495 10.1977 11.5491 8.40245 11.5491H4.04209C2.24693 11.5491 1.34899 11.5495 0.825297 11.1722C0.368476 10.8429 0.0719189 10.3361 0.00791442 9.77665C-0.0654466 9.13534 0.372947 8.35179 1.2501 6.78544L3.43077 2.89286C4.34328 1.26337 4.79932 0.448048 5.39952 0.177041ZM6.22276 7.67411C5.80862 7.67411 5.47288 8.01 5.47276 8.42411C5.47276 8.83833 5.80854 9.17411 6.22276 9.17411C6.63679 9.1739 6.97276 8.8382 6.97276 8.42411C6.97263 8.01013 6.63672 7.67432 6.22276 7.67411ZM6.22276 4.17411C5.94669 4.17411 5.72288 4.39808 5.72276 4.67411V6.67411C5.72276 6.95025 5.94662 7.17411 6.22276 7.17411C6.49872 7.1739 6.72276 6.95012 6.72276 6.67411V4.67411C6.72263 4.3982 6.49865 4.17432 6.22276 4.17411Z" fill="#AC4124"/>' +
    '</svg></span>';
  var DEBUG_MULTI_VARIANT =
    typeof window !== 'undefined' &&
    window.TT_DEBUG_MULTI_VARIANT !== false &&
    (function () {
      try {
        return localStorage.getItem('TT_DEBUG_MULTI_VARIANT') !== '0';
      } catch (error) {
        return true;
      }
    })();

  function isMultiVariantElement(el) {
    return !!(el && el.closest && el.closest('.custom-variant-product-info'));
  }

  function shouldDebugMultiVariant(context, el) {
    if (!DEBUG_MULTI_VARIANT) return false;
    if (context) return context === 'multi-variant';
    return isMultiVariantElement(el);
  }

  function debugMultiVariant(step, details, context) {
    if (!shouldDebugMultiVariant(context, details && details.inputEl)) return;

    if (details && typeof details === 'object') {
      console.log('[TT:multi-variant]', step, details);
      return;
    }

    console.log('[TT:multi-variant]', step, details !== undefined ? details : '');
  }

  function getInputInventoryDebug(inputEl) {
    if (!inputEl) return null;

    return {
      value: inputEl.value,
      max: inputEl.getAttribute('max'),
      dataInventoryMax: inputEl.getAttribute('data-inventory-max'),
      min: inputEl.getAttribute('min'),
      step: inputEl.getAttribute('step'),
      pack: inputEl.getAttribute('data-title')
    };
  }

  function getSelectionDebug(selection) {
    if (!selection) return null;

    return {
      variantId: selection.variantId,
      name: selection.name,
      quantity: selection.quantity,
      packSize: selection.packSize,
      input: getInputInventoryDebug(selection.inputEl)
    };
  }

  function getVariantDebug(variant) {
    if (!variant) return null;

    return {
      id: variant.id,
      available: variant.available,
      inventory_management: variant.inventory_management,
      inventory_quantity: variant.inventory_quantity,
      inventory_policy: variant.inventory_policy,
      hasReliableInventoryQuantity: hasReliableInventoryQuantity(variant)
    };
  }

  function logMultiVariantInputsSnapshot(label) {
    if (!DEBUG_MULTI_VARIANT) return;

    var root = document.querySelector('.custom-variant-product-info');
    if (!root) {
      debugMultiVariant(label + ': root not found');
      return;
    }

    var inputs = Array.from(root.querySelectorAll('.quantity-variant-product')).map(function (wrapper) {
      var input = wrapper.querySelector('.quantity-input');
      return {
        variantId: wrapper.getAttribute('data-variant-id'),
        variantName: wrapper.getAttribute('data-variant-name'),
        input: getInputInventoryDebug(input)
      };
    });

    debugMultiVariant(label, { inputCount: inputs.length, inputs: inputs }, 'multi-variant');
  }

  function parseInventoryNumber(value) {
    if (value === null || value === undefined || value === '') return null;

    var parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function resolveStorefrontNodeQuantity(node) {
    if (!node) return null;

    if (node.currentlyNotInStock) {
      return null;
    }

    var qty = parseInventoryNumber(node.quantityAvailable);
    var total = parseInventoryNumber(node.product && node.product.totalInventory);

    if (qty === null && total === null) {
      return null;
    }

    return Math.max(0, qty || 0, total || 0);
  }

  function normalizeStorefrontInventory(value) {
    if (value === null || value === undefined) return null;

    var parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  }

  function getMultiVariantStorefrontConfig() {
    return window.TTMultiVariantStorefront || null;
  }

  function fetchStorefrontVariantInventoryMap(variantIds) {
    var config = getMultiVariantStorefrontConfig();
    if (!config || !variantIds.length) {
      return Promise.resolve({});
    }

    var chunks = [];
    for (var i = 0; i < variantIds.length; i += 250) {
      chunks.push(variantIds.slice(i, i + 250));
    }

    return Promise.all(
      chunks.map(function (chunk) {
        var gids = chunk.map(function (id) {
          return 'gid://shopify/ProductVariant/' + id;
        });

        return fetch('https://' + config.shopDomain + '/api/2023-10/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': config.token
          },
          body: JSON.stringify({
            query:
              'query ($ids: [ID!]!) { nodes(ids: $ids) { ... on ProductVariant { id availableForSale quantityAvailable currentlyNotInStock product { totalInventory } } } }',
            variables: { ids: gids }
          })
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (json) {
            var map = {};

            ((json.data && json.data.nodes) || []).forEach(function (node) {
              if (!node || !node.id) return;

              var numericId = String(node.id).replace('gid://shopify/ProductVariant/', '');
              map[numericId] = resolveStorefrontNodeQuantity(node);
            });

            if (shouldDebugMultiVariant('multi-variant')) {
              debugMultiVariant('fetchStorefrontVariantInventoryMap chunk', map, 'multi-variant');
            }

            return map;
          })
          .catch(function (error) {
            debugMultiVariant('fetchStorefrontVariantInventoryMap failed', { error: String(error) }, 'multi-variant');
            return {};
          });
      })
    ).then(function (maps) {
      return Object.assign.apply(null, [{}].concat(maps));
    });
  }

  function shouldPreserveUserQuantity(context) {
    return context === 'multi-variant';
  }

  function applyStorefrontInventoryToWrapper(wrapper, availableQty) {
    var input = wrapper.querySelector('.quantity-input');
    if (!input) return;

    var packSize = getPackSize(input);
    var untrackedOrContinue = availableQty === null || availableQty === undefined;
    var normalizedQty = untrackedOrContinue ? 999999 : normalizeStorefrontInventory(availableQty);
    var previousMax = input.getAttribute('max');
    var currentQty = parseInt(input.value, 10) || 0;
    var quantityRow = wrapper.closest('.quantity-row');
    var item = wrapper.closest('.variant-product-item');
    var availableEl = quantityRow ? quantityRow.querySelector('.available.customer') : null;
    var plusBtn = wrapper.querySelector('.plus-quantity');
    var isPurchasable = untrackedOrContinue || normalizedQty >= packSize;

    input.setAttribute('max', normalizedQty);
    input.setAttribute('data-inventory-max', normalizedQty);
    input.setAttribute('data-inventory-source', 'storefront-api');

    if (plusBtn) {
      var nextQty = parseInt(input.value, 10) || 0;
      plusBtn.classList.toggle('limit', !untrackedOrContinue && (normalizedQty <= 0 || nextQty >= normalizedQty));
    }

    if (availableEl) {
      availableEl.textContent = isPurchasable ? 'Available' : 'Unavailable';
    }

    wrapper.style.display = isPurchasable || currentQty > 0 ? '' : 'none';

    if (item) {
      item.querySelectorAll('.klavio-and-replacement-desktop, .klavio-and-replacement').forEach(function (el) {
        el.style.display = isPurchasable ? 'none' : '';
      });
    }

    if (shouldDebugMultiVariant(null, input)) {
      debugMultiVariant('applyStorefrontInventoryToWrapper', {
        variantId: wrapper.getAttribute('data-variant-id'),
        variantName: wrapper.getAttribute('data-variant-name'),
        packSize: packSize,
        availableQty: availableQty,
        normalizedQty: normalizedQty,
        beforeMax: previousMax,
        after: getInputInventoryDebug(input)
      }, 'multi-variant');
    }
  }

  function syncMultiVariantStorefrontInventory() {
    var root = document.querySelector('.custom-variant-product-info');
    if (!root) return Promise.resolve();

    var wrappers = Array.from(root.querySelectorAll('.quantity-variant-product'));
    var variantIds = wrappers
      .map(function (wrapper) {
        return wrapper.getAttribute('data-variant-id');
      })
      .filter(Boolean);

    if (!variantIds.length) {
      return Promise.resolve();
    }

    debugMultiVariant(
      'syncMultiVariantStorefrontInventory:start',
      { variantCount: variantIds.length },
      'multi-variant'
    );

    return fetchStorefrontVariantInventoryMap(variantIds).then(function (inventoryMap) {
      wrappers.forEach(function (wrapper) {
        var variantId = String(wrapper.getAttribute('data-variant-id'));
        if (inventoryMap[variantId] === undefined) return;

        applyStorefrontInventoryToWrapper(wrapper, inventoryMap[variantId]);
      });

      logMultiVariantInputsSnapshot('syncMultiVariantStorefrontInventory:done');
    });
  }

  var CART_LINE_LIMIT_MESSAGE =
    'Shopify Order limit in 450 line items in the cart reached, please break into two orders.';

  function buildErrorMessage(labels) {
    return (
      'The following Variant Product/s is already sold out:\n' +
      labels.join('\n') +
      '\nPlease deselect this Product/s to process.'
    );
  }

  function getOosMessageLabels(soldOutItems) {
    var seenVariantIds = {};
    var labels = [];

    (soldOutItems || []).forEach(function (item) {
      if (!item) return;

      var variantId = String(item.variantId || '');
      if (!variantId || seenVariantIds[variantId]) return;

      seenVariantIds[variantId] = true;
      if (item.name) {
        labels.push(item.name);
      }
    });

    return labels;
  }

  function selectionIsSoldOut(soldOutItems, selection) {
    var variantId = String(selection.variantId);

    return (soldOutItems || []).some(function (item) {
      return item && String(item.variantId) === variantId;
    });
  }

  function getOosListingName(selection) {
    var name = (selection && selection.name) || 'Product';
    if (!selection || !selection.inputEl) return name;

    var item = selection.inputEl.closest('.variant-product-item, .quick-order__product-item');
    if (!item) return name;

    var skuEl = item.querySelector('.product-meta-wrap .sku, .quick-order__product-item-sku');
    if (!skuEl) return name;

    var skuText = (skuEl.textContent || '').replace(/\s+/g, ' ').trim();
    var sku = skuText.replace(/^SKU:\s*/i, '').trim();
    if (!sku || name.indexOf(sku) !== -1) return name;

    return name + ' — SKU: ' + sku;
  }

  function getPackSize(input) {
    if (!input) return 1;

    var match = (input.getAttribute('data-title') || '').match(/pack-of-(\d+)/i);
    if (match) return parseInt(match[1], 10) || 1;

    var wrapper = input.closest('[data-additional-multiplier]');
    if (wrapper) {
      var multiplier = parseInt(wrapper.getAttribute('data-additional-multiplier'), 10);
      if (!isNaN(multiplier) && multiplier > 0) return multiplier;
    }

    return 1;
  }

  function getVariantName(wrapper) {
    if (!wrapper) return 'Product';

    var explicitName = wrapper.getAttribute('data-variant-name');
    if (explicitName) return explicitName.trim();

    var item = wrapper.closest('.variant-product-item, .quick-order__product-item');
    if (!item) return 'Product';

    var titleEl = item.querySelector('.product-meta-wrap .title, .quick-order__product-item-title');
    if (!titleEl) return 'Product';

    var clone = titleEl.cloneNode(true);
    clone.querySelectorAll('.new-chip').forEach(function (chip) {
      chip.remove();
    });

    return clone.textContent.replace(/\s+/g, ' ').trim() || 'Product';
  }

  function fetchVariantData(variantId) {
    return fetch('/variants/' + variantId + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) return null;
        return response.json();
      })
      .catch(function () {
        return null;
      });
  }

  function fetchCartItems() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) return [];
        return response.json();
      })
      .then(function (cart) {
        return (cart.items || []).map(function (item) {
          return {
            id: String(item.variant_id),
            quantity: item.quantity
          };
        });
      })
      .catch(function () {
        return [];
      });
  }

  function getCartQuantity(cartItems, variantId) {
    var match = cartItems.find(function (item) {
      return String(item.id) === String(variantId);
    });

    return match ? match.quantity : 0;
  }

  function hasReliableInventoryQuantity(variant) {
    return (
      variant &&
      variant.inventory_management &&
      variant.inventory_quantity !== undefined &&
      variant.inventory_quantity !== null &&
      Number.isFinite(parseInt(variant.inventory_quantity, 10))
    );
  }

  function getVariantInventoryQuantity(variant) {
    if (!hasReliableInventoryQuantity(variant)) return null;

    return Math.max(0, parseInt(variant.inventory_quantity, 10));
  }

  function getBaselineInventoryMax(inputEl) {
    if (!inputEl) return null;

    var fromData = parseInt(inputEl.getAttribute('data-inventory-max'), 10);
    if (Number.isFinite(fromData)) return Math.max(0, fromData);

    var fromMax = parseInt(inputEl.getAttribute('max'), 10);
    if (Number.isFinite(fromMax)) return Math.max(0, fromMax);

    return null;
  }

  function normalizeAvailableQuantity(quantity) {
    var normalized = parseInt(quantity, 10);
    return Number.isFinite(normalized) ? Math.max(0, normalized) : 0;
  }

  function resolveAvailableQuantity(variant, cartItems, variantId, inputEl) {
    if (!variant) {
      if (shouldDebugMultiVariant(null, inputEl)) {
        debugMultiVariant('resolveAvailableQuantity → 0 (no variant)', {
          variantId: variantId,
          input: getInputInventoryDebug(inputEl)
        }, 'multi-variant');
      }
      return 0;
    }

    if (!variant.inventory_management || variant.inventory_policy === 'continue') {
      if (shouldDebugMultiVariant(null, inputEl)) {
        debugMultiVariant('resolveAvailableQuantity → null (untracked or continue)', {
          variantId: variantId,
          variant: getVariantDebug(variant),
          input: getInputInventoryDebug(inputEl)
        }, 'multi-variant');
      }
      return null;
    }

    var inCart = getCartQuantity(cartItems, variantId);
    var apiInventory = getVariantInventoryQuantity(variant);

    if (apiInventory !== null) {
      var fromApi = normalizeAvailableQuantity(apiInventory - inCart);
      if (shouldDebugMultiVariant(null, inputEl)) {
        debugMultiVariant('resolveAvailableQuantity → from API', {
          variantId: variantId,
          apiInventory: apiInventory,
          inCart: inCart,
          resolved: fromApi,
          variant: getVariantDebug(variant),
          input: getInputInventoryDebug(inputEl)
        }, 'multi-variant');
      }
      return fromApi;
    }

    var baseline = getBaselineInventoryMax(inputEl);
    if (baseline !== null) {
      var fromBaseline = normalizeAvailableQuantity(baseline - inCart);
      if (shouldDebugMultiVariant(null, inputEl)) {
        debugMultiVariant('resolveAvailableQuantity → from baseline (API qty missing)', {
          variantId: variantId,
          baseline: baseline,
          inCart: inCart,
          resolved: fromBaseline,
          variant: getVariantDebug(variant),
          input: getInputInventoryDebug(inputEl)
        }, 'multi-variant');
      }
      return fromBaseline;
    }

    if (shouldDebugMultiVariant(null, inputEl)) {
      debugMultiVariant('resolveAvailableQuantity → null (unknown qty, do not treat as sold out)', {
        variantId: variantId,
        inCart: inCart,
        variant: getVariantDebug(variant),
        input: getInputInventoryDebug(inputEl)
      }, 'multi-variant');
    }

    return null;
  }

  function getAvailableQuantity(variant, cartItems, variantId, inputEl) {
    return resolveAvailableQuantity(variant, cartItems, variantId, inputEl);
  }

  function isSelectionSoldOut(selection, variant, cartItems) {
    if (!variant) {
      if (shouldDebugMultiVariant(null, selection && selection.inputEl)) {
        debugMultiVariant('isSelectionSoldOut → true (no variant data)', getSelectionDebug(selection), 'multi-variant');
      }
      return true;
    }

    var availableQty = resolveAvailableQuantity(
      variant,
      cartItems,
      selection.variantId,
      selection.inputEl
    );

    if (availableQty === null) {
      if (shouldDebugMultiVariant(null, selection.inputEl)) {
        debugMultiVariant('isSelectionSoldOut → false (qty unknown, variant available)', {
          selection: getSelectionDebug(selection),
          variant: getVariantDebug(variant)
        }, 'multi-variant');
      }
      return false;
    }

    var packSize = selection.packSize || 1;
    var soldOut = false;
    var reason = '';

    if (availableQty <= 0) {
      soldOut = true;
      reason = 'availableQty <= 0';
    } else if (availableQty < packSize) {
      soldOut = true;
      reason = 'availableQty < packSize';
    } else if (selection.quantity > availableQty) {
      soldOut = true;
      reason = 'selection.quantity > availableQty';
    }

    if (shouldDebugMultiVariant(null, selection.inputEl)) {
      debugMultiVariant('isSelectionSoldOut → ' + soldOut + (reason ? ' (' + reason + ')' : ''), {
        selection: getSelectionDebug(selection),
        availableQty: availableQty,
        packSize: packSize,
        variant: getVariantDebug(variant)
      }, 'multi-variant');
    }

    return soldOut;
  }

  function validateSelections(selections, cartItems, debugContext) {
    if (!selections.length) {
      return Promise.resolve([]);
    }

    return Promise.all(
      selections.map(function (selection) {
        return fetchVariantData(selection.variantId).then(function (variant) {
          if (shouldDebugMultiVariant(debugContext, selection.inputEl)) {
            debugMultiVariant('fetchVariantData /variants/' + selection.variantId + '.js', {
              selection: getSelectionDebug(selection),
              variant: getVariantDebug(variant),
              rawVariant: variant
            }, 'multi-variant');
          }

          return {
            selection: selection,
            variant: variant,
            soldOut: isSelectionSoldOut(selection, variant, cartItems)
          };
        });
      })
    ).then(function (results) {
      if (shouldDebugMultiVariant(debugContext)) {
        debugMultiVariant('validateSelections summary', {
          cartItems: cartItems,
          results: results.map(function (result) {
            return {
              selection: getSelectionDebug(result.selection),
              soldOut: result.soldOut,
              variant: getVariantDebug(result.variant)
            };
          }),
          soldOutItems: results
            .filter(function (result) {
              return result.soldOut;
            })
            .map(function (result) {
              return {
                variantId: result.selection.variantId,
                name: getOosListingName(result.selection)
              };
            })
        }, 'multi-variant');
      }

      return results
        .filter(function (result) {
          return result.soldOut;
        })
        .map(function (result) {
          return {
            variantId: result.selection.variantId,
            name: getOosListingName(result.selection)
          };
        });
    });
  }

  function applyInventoryToSelection(selection, variant, cartItems, forceReset, debugContext) {
    if (!selection.inputEl || !variant) return;

    var previousMax = selection.inputEl.getAttribute('max');
    var previousValue = selection.inputEl.value;
    var availableQty = resolveAvailableQuantity(
      variant,
      cartItems,
      selection.variantId,
      selection.inputEl
    );
    var currentQty = parseInt(selection.inputEl.value, 10) || 0;
    var wrapper = selection.inputEl.closest('.quantity-variant-product');
    var plusBtn = wrapper
      ? wrapper.querySelector('.plus-quantity, .quick-order-quantity-update.plus-quantity')
      : null;
    var plusLimitQty = availableQty;
    var action = 'no-op';

    if (availableQty !== null) {
      action = 'set max=' + availableQty;
      selection.inputEl.setAttribute('max', availableQty);

      if (!shouldPreserveUserQuantity(debugContext) && (forceReset || currentQty > availableQty)) {
        action += ', clamp qty to ' + availableQty;
        selection.inputEl.value = availableQty;

        if (typeof updateTotalPrice === 'function') {
          updateTotalPrice();
        }
      }
    } else {
      action = 'keep SSR max (qty unknown from API)';
      plusLimitQty = getBaselineInventoryMax(selection.inputEl);
    }

    if (plusBtn) {
      var nextQty = parseInt(selection.inputEl.value, 10) || 0;
      var limitQty = plusLimitQty === null ? getBaselineInventoryMax(selection.inputEl) : plusLimitQty;
      plusBtn.classList.toggle(
        'limit',
        limitQty !== null && (limitQty <= 0 || nextQty >= limitQty)
      );
    }

    if (shouldDebugMultiVariant(debugContext, selection.inputEl)) {
      debugMultiVariant('applyInventoryToSelection', {
        action: action,
        forceReset: forceReset,
        selection: getSelectionDebug(selection),
        variant: getVariantDebug(variant),
        availableQty: availableQty,
        before: { max: previousMax, value: previousValue },
        after: getInputInventoryDebug(selection.inputEl),
        plusLimit: plusLimitQty
      }, 'multi-variant');

      if (previousMax !== selection.inputEl.getAttribute('max')) {
        console.trace('[TT:multi-variant] max changed', {
          variantId: selection.variantId,
          from: previousMax,
          to: selection.inputEl.getAttribute('max')
        });
      }
    }
  }

  function getAllTrackedSelections(root) {
    root = root || document;

    return Array.from(root.querySelectorAll('.quantity-variant-product'))
      .map(function (wrapper) {
        var input = wrapper.querySelector('.quantity-input, .quick-order-quantity-input');
        if (!input) return null;

        return {
          variantId: wrapper.getAttribute('data-variant-id'),
          quantity: parseInt(input.value, 10) || 0,
          name: getVariantName(wrapper),
          packSize: getPackSize(input),
          inputEl: input
        };
      })
      .filter(Boolean);
  }

  function syncInventoryDom(context, cartItems, soldOutSelections) {
    var elements = getContextElements(context);
    if (!elements.root) return Promise.resolve();

    var soldOutIds = {};
    soldOutSelections.forEach(function (selection) {
      soldOutIds[String(selection.variantId)] = true;
    });

    var allTracked = getAllTrackedSelections(elements.root);
    var trackedSelections = allTracked.filter(function (selection) {
      return selection.quantity > 0 || soldOutIds[String(selection.variantId)] === true;
    });

    if (shouldDebugMultiVariant(context)) {
      debugMultiVariant('syncInventoryDom', {
        allTrackedCount: allTracked.length,
        syncCount: trackedSelections.length,
        soldOutIds: soldOutIds,
        syncing: trackedSelections.map(getSelectionDebug)
      }, context);
    }

    if (!trackedSelections.length) {
      if (shouldDebugMultiVariant(context)) {
        debugMultiVariant('syncInventoryDom skipped (nothing to sync)', null, context);
      }
      return Promise.resolve();
    }

    return Promise.all(
      trackedSelections.map(function (selection) {
        return fetchVariantData(selection.variantId).then(function (variant) {
          applyInventoryToSelection(
            selection,
            variant,
            cartItems,
            soldOutIds[String(selection.variantId)] === true,
            context
          );
        });
      })
    ).then(function () {
      if (context === 'multi-variant') {
        syncMultiVariantOosUi(soldOutSelections);
      } else if (context === 'quick-order') {
        syncQuickOrderOosUi(soldOutSelections);
      }

      if (shouldDebugMultiVariant(context)) {
        logMultiVariantInputsSnapshot('syncInventoryDom:after');
      }
    });
  }

  function getVariantProductItemFromSelection(selection) {
    if (!selection || !selection.inputEl) return null;

    var wrapper = selection.inputEl.closest('.quantity-variant-product');
    return wrapper ? wrapper.closest('.variant-product-item') : null;
  }

  function getAvailabilityLabelForWrapper(wrapper) {
    if (!wrapper) return 'Unavailable';

    var input = wrapper.querySelector('.quantity-input');
    var packSize = getPackSize(input);
    var max = parseInt(input && input.getAttribute('max'), 10);

    if (!Number.isFinite(max)) return 'Unavailable';

    return max >= packSize ? 'Available' : 'Unavailable';
  }

  function applyOosItemUi(item) {
    if (!item) return;

    var availableEl = item.querySelector('.quantity-row .available.customer');
    var skuEl = item.querySelector('.product-meta-wrap .sku');

    item.classList.add(OOS_UI_ITEM_CLASS);

    if (availableEl) {
      availableEl.classList.add(OOS_UI_AVAILABILITY_CLASS);
      availableEl.textContent = 'Unavailable';
    }

    if (skuEl && !skuEl.querySelector('.oos-warning-icon')) {
      var skuText = skuEl.getAttribute('data-sku-text') || skuEl.textContent.trim();

      if (!skuEl.getAttribute('data-sku-text')) {
        skuEl.setAttribute('data-sku-text', skuText);
      }

      skuEl.classList.add(OOS_UI_AVAILABILITY_CLASS);
      skuEl.innerHTML = skuText + OOS_WARNING_ICON_HTML;
    }
  }

  function clearOosItemUi(item, wrapper) {
    if (!item) return;

    var availableEl = item.querySelector('.quantity-row .available.customer');
    var skuEl = item.querySelector('.product-meta-wrap .sku');

    item.classList.remove(OOS_UI_ITEM_CLASS);

    if (availableEl) {
      availableEl.classList.remove(OOS_UI_AVAILABILITY_CLASS);
      availableEl.textContent = getAvailabilityLabelForWrapper(wrapper);
    }

    if (skuEl) {
      skuEl.classList.remove(OOS_UI_AVAILABILITY_CLASS);

      var skuText = skuEl.getAttribute('data-sku-text');
      if (skuText) {
        skuEl.textContent = skuText;
        skuEl.removeAttribute('data-sku-text');
      }
    }
  }

  function scrollVariantListToItem(listContainer, item) {
    if (!listContainer || !item) return;

    var containerRect = listContainer.getBoundingClientRect();
    var itemRect = item.getBoundingClientRect();
    var currentScroll = listContainer.scrollTop;
    var itemTop = currentScroll + (itemRect.top - containerRect.top);
    var itemHeight = item.offsetHeight;
    var containerHeight = listContainer.clientHeight;
    var maxScroll = Math.max(0, listContainer.scrollHeight - containerHeight);
    var targetScroll = itemTop - (containerHeight - itemHeight) / 2;

    targetScroll = Math.max(0, Math.min(targetScroll, maxScroll));

    listContainer.scrollTo({
      top: targetScroll,
      behavior: 'smooth'
    });
  }

  function syncMultiVariantOosUi(soldOutSelections, options) {
    options = options || {};
    var root = document.querySelector('.custom-variant-product-info');
    if (!root) return;

    var listContainer = root.querySelector('.variant-product-list');
    var soldOutIds = {};

    (soldOutSelections || []).forEach(function (selection) {
      soldOutIds[String(selection.variantId)] = selection;
    });

    var firstNewOosItem = null;

    root.querySelectorAll('.variant-product-item').forEach(function (item) {
      var wrapper = item.querySelector('.quantity-variant-product');
      if (!wrapper) return;

      var variantId = String(wrapper.getAttribute('data-variant-id'));
      var selection = soldOutIds[variantId];
      var wasOos = item.classList.contains(OOS_UI_ITEM_CLASS);

      if (selection) {
        if (!wasOos && !firstNewOosItem) {
          firstNewOosItem = item;
        }

        applyOosItemUi(item);
        oosUiAppliedVariantIds[variantId] = true;
        return;
      }

      if (oosUiAppliedVariantIds[variantId]) {
        clearOosItemUi(item, wrapper);
        delete oosUiAppliedVariantIds[variantId];
      }
    });

    if (firstNewOosItem && options.scrollToFirst !== false) {
      scrollVariantListToItem(listContainer, firstNewOosItem);
    }

    if (shouldDebugMultiVariant('multi-variant')) {
      debugMultiVariant('syncMultiVariantOosUi', {
        soldOutCount: (soldOutSelections || []).length,
        scrolledToFirst: !!firstNewOosItem,
        appliedIds: Object.keys(oosUiAppliedVariantIds)
      }, 'multi-variant');
    }
  }

  function applyQuickOrderOosItemUi(item) {
    if (!item) return;

    var skuEl = item.querySelector('.variant-product-sku');
    var qtyWrap = item.querySelector('.quantity-variant-product');

    item.classList.add(OOS_UI_ITEM_CLASS);
    item.setAttribute('data-in-stock', 'false');

    if (qtyWrap) {
      qtyWrap.classList.add('is-out-of-stock');
    }

    if (skuEl && !skuEl.querySelector('.oos-warning-icon')) {
      var skuText = skuEl.getAttribute('data-sku-text') || skuEl.textContent.trim();

      if (!skuEl.getAttribute('data-sku-text')) {
        skuEl.setAttribute('data-sku-text', skuText);
      }

      skuEl.classList.add(OOS_UI_AVAILABILITY_CLASS);
      skuEl.innerHTML = OOS_WARNING_ICON_HTML + skuText;
    }
  }

  function clearQuickOrderOosItemUi(item) {
    if (!item) return;

    var skuEl = item.querySelector('.variant-product-sku');
    var qtyWrap = item.querySelector('.quantity-variant-product');
    var input = item.querySelector('.quick-order-quantity-input');
    var stockMax = input ? parseInt(input.getAttribute('max'), 10) : 0;
    var hasStock = Number.isFinite(stockMax) && stockMax > 0;

    item.classList.remove(OOS_UI_ITEM_CLASS);
    item.setAttribute('data-in-stock', hasStock ? 'true' : 'false');

    if (qtyWrap) {
      qtyWrap.classList.toggle('is-out-of-stock', !hasStock);
    }

    if (skuEl) {
      skuEl.classList.remove(OOS_UI_AVAILABILITY_CLASS);

      var skuText = skuEl.getAttribute('data-sku-text');
      if (skuText) {
        skuEl.textContent = skuText;
        skuEl.removeAttribute('data-sku-text');
      }
    }
  }

  function getVisibleQuickOrderList() {
    var allList = document.getElementById('product-container');
    var selectedList = document.getElementById('product-container-selected');

    if (allList && allList.offsetParent !== null) return allList;
    if (selectedList && selectedList.offsetParent !== null) return selectedList;

    return allList || selectedList;
  }

  function findFirstSoldOutDomItem(root, itemSelector, soldOutIds) {
    if (!root || !soldOutIds) return null;

    var match = null;

    root.querySelectorAll(itemSelector).forEach(function (item) {
      if (match) return;

      var wrapper = item.querySelector('.quantity-variant-product');
      if (!wrapper) return;

      var variantId = String(wrapper.getAttribute('data-variant-id'));
      if (soldOutIds[variantId]) {
        match = item;
      }
    });

    return match;
  }

  function syncQuickOrderOosUi(soldOutSelections, options) {
    options = options || {};
    var root = document.querySelector('.section-quick-order');
    if (!root) return;

    var soldOutIds = {};

    (soldOutSelections || []).forEach(function (selection) {
      soldOutIds[String(selection.variantId)] = selection;
    });

    var firstNewOosItem = null;
    var clearedAnyOos = false;
    var seenVariantIds = {};

    root.querySelectorAll('.quick-order__product-item').forEach(function (item) {
      var wrapper = item.querySelector('.quantity-variant-product');
      if (!wrapper) return;

      var variantId = String(wrapper.getAttribute('data-variant-id'));
      var selection = soldOutIds[variantId];
      var wasOos = item.classList.contains(OOS_UI_ITEM_CLASS);

      if (selection) {
        if (!wasOos && !firstNewOosItem) {
          firstNewOosItem = item;
        }

        applyQuickOrderOosItemUi(item);
        quickOrderOosUiAppliedVariantIds[variantId] = true;
        seenVariantIds[variantId] = true;
        return;
      }

      if (quickOrderOosUiAppliedVariantIds[variantId]) {
        clearQuickOrderOosItemUi(item);
        clearedAnyOos = true;
      }
    });

    Object.keys(quickOrderOosUiAppliedVariantIds).forEach(function (variantId) {
      if (!seenVariantIds[variantId] && !soldOutIds[variantId]) {
        delete quickOrderOosUiAppliedVariantIds[variantId];
      }
    });

    if (options.scrollToFirst === false) return;

    var scrollTarget = firstNewOosItem;

    if (!scrollTarget && clearedAnyOos && Object.keys(soldOutIds).length) {
      scrollTarget = findFirstSoldOutDomItem(root, '.quick-order__product-item', soldOutIds);
    }

    if (scrollTarget) {
      scrollVariantListToItem(getVisibleQuickOrderList(), scrollTarget);
    }
  }

  function clearOosUiForContext(context) {
    if (context === 'multi-variant') {
      syncMultiVariantOosUi([], { scrollToFirst: false });
      return;
    }

    if (context === 'quick-order') {
      syncQuickOrderOosUi([], { scrollToFirst: false });
    }
  }

  function getCartLimitErrorEl(elements) {
    return (elements && (elements.cartLimitEl || elements.errorEl)) || null;
  }

  function getOosErrorEl(elements) {
    return (elements && (elements.oosEl || elements.errorEl)) || null;
  }

  function getOosErrorTargets(container) {
    if (
      container &&
      container.classList &&
      container.classList.contains('quick-order-oos-message-wrap')
    ) {
      return Array.from(
        document.querySelectorAll('.section-quick-order .quick-order-oos-message-wrap')
      );
    }

    return container ? [container] : [];
  }

  function applyOosErrorToContainer(container, labels) {
    if (!container || !labels.length) return;

    var productList = container.querySelector('.oos-product-list');

    if (productList) {
      productList.textContent = '';

      labels.forEach(function (name) {
        var line = document.createElement('div');
        line.textContent = name;
        productList.appendChild(line);
      });

      container.setAttribute('data-error-type', 'oos');
      container.hidden = false;
      container.classList.add('show');
      return;
    }

    container.setAttribute('data-error-type', 'oos');
    container.textContent = buildErrorMessage(labels);
    container.style.display = 'block';
    container.style.whiteSpace = 'pre-line';
    container.classList.add('show');
  }

  function showError(container, names) {
    showOosError(container, names);
  }

  function showOosError(container, soldOutItems) {
    var labels = getOosMessageLabels(soldOutItems);
    if (!labels.length) return;

    getOosErrorTargets(container).forEach(function (target) {
      applyOosErrorToContainer(target, labels);
    });
  }

  function isSimplePdpCartLimitNotice(container) {
    return !!(container && container.classList && container.classList.contains('simple-pdp-cart-limit-notice'));
  }

  function showCartLineLimitError(container, message) {
    if (!container) return;

    container.setAttribute('data-error-type', 'cart-limit');
    container.classList.add('show');

    if (isSimplePdpCartLimitNotice(container)) {
      container.hidden = false;
      container.style.display = 'flex';
      container.style.whiteSpace = '';
      return;
    }

    container.textContent = message || CART_LINE_LIMIT_MESSAGE;
    container.style.display = 'block';
    container.style.whiteSpace = 'pre-line';
  }

  function hideCartLineLimitError(container) {
    if (!container) return;

    if (isSimplePdpCartLimitNotice(container)) {
      container.removeAttribute('data-error-type');
      container.classList.remove('show');
      container.hidden = true;
      container.style.display = 'none';
      container.style.whiteSpace = '';
      return;
    }

    if (container.getAttribute('data-error-type') !== 'cart-limit') return;

    container.removeAttribute('data-error-type');
    container.classList.remove('show');
    container.style.whiteSpace = '';
    container.textContent = '';
    container.style.display = 'none';
  }

  function hideError(container) {
    hideOosError(container);
  }

  function hideOosError(container) {
    if (!container) return;

    getOosErrorTargets(container).forEach(function (target) {
      if (target.getAttribute('data-error-type') !== 'oos') return;

      var productList = target.querySelector('.oos-product-list');

      target.removeAttribute('data-error-type');
      target.classList.remove('show');

      if (productList) {
        productList.textContent = '';
        target.hidden = true;
        return;
      }

      target.textContent = '';
      target.style.display = 'none';
      target.style.whiteSpace = '';
    });
  }

  function getCartLineLimitMessage(context) {
    if (context === 'multi-variant') {
      if (typeof checkCartItemLines === 'function') {
        var multiVariantStatus = checkCartItemLines();
        if (multiVariantStatus && !multiVariantStatus.status) {
          return multiVariantStatus.message || CART_LINE_LIMIT_MESSAGE;
        }
      }
      return null;
    }

    if (context === 'live-product') {
      var liveCartLinesEl =
        document.querySelector('.live-variant-product-info .product-buy-buttons-cart-lines') ||
        document.querySelector('.product-buy-buttons-cart-lines');

      if (liveCartLinesEl) {
        var liveCartCount = parseInt(liveCartLinesEl.getAttribute('data-cart-lines'), 10) || 0;
        var liveForm = document.querySelector('.live-variant-product-info form.product-form');
        var liveSelection = liveForm ? getLiveProductFormSelection(liveForm) : null;
        var liveVariantId = liveSelection && liveSelection.variantId;
        var liveInCart = false;

        if (liveVariantId && typeof getCartVariantIdSet === 'function') {
          liveInCart = !!getCartVariantIdSet()[String(liveVariantId)];
        }

        if (!liveInCart && liveCartCount >= 450) {
          return CART_LINE_LIMIT_MESSAGE;
        }
      }
      return null;
    }

    if (context === 'quick-order') {
      if (typeof quickOrderValidateAddAllCartLineLimit === 'function') {
        return quickOrderValidateAddAllCartLineLimit();
      }
    }

    return null;
  }

  function setButtonsState(buttons, disabled) {
    buttons.forEach(function (button) {
      if (!button) return;

      if (button.tagName === 'BUTTON' || button.tagName === 'INPUT') {
        button.disabled = disabled;
      } else {
        button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        button.classList.toggle('is-disabled', disabled);
        button.style.pointerEvents = disabled ? 'none' : '';
        button.style.opacity = disabled ? '0.55' : '';
        button.style.cursor = disabled ? 'not-allowed' : '';
      }
    });
  }

  function getMultiVariantSelections(root) {
    root = root || document;

    var selections = Array.from(root.querySelectorAll('.quantity-variant-product'))
      .map(function (wrapper) {
        var input = wrapper.querySelector('.quantity-input, .quick-order-quantity-input');
        if (!input) return null;

        var quantity = parseInt(input.value, 10) || 0;
        if (quantity <= 0) return null;

        return {
          variantId: wrapper.getAttribute('data-variant-id'),
          quantity: quantity,
          name: getVariantName(wrapper),
          packSize: getPackSize(input),
          inputEl: input
        };
      })
      .filter(Boolean);

    if (DEBUG_MULTI_VARIANT) {
      debugMultiVariant('getMultiVariantSelections', {
        count: selections.length,
        selections: selections.map(getSelectionDebug)
      }, 'multi-variant');
    }

    return selections;
  }

  function getQuickOrderSelections() {
    var selections = [];
    var seen = {};

    try {
      var stored = localStorage.getItem('currentProducts');
      if (stored) {
        JSON.parse(stored).forEach(function (product) {
          var quantity = parseInt(product.quantity, 10) || 0;
          if (quantity <= 0) return;

          var variantId = String(product.variantID);
          if (seen[variantId]) return;

          seen[variantId] = true;
          selections.push({
            variantId: variantId,
            quantity: quantity,
            name: product.title || 'Product',
            packSize: parseInt(product.additionalMultiply, 10) || 1
          });
        });
      }
    } catch (error) {
      selections = [];
    }

    return selections;
  }

  function getLiveProductFormSelection(form) {
    var variantInput = form.querySelector('[name="id"]');
    var quantityInput = form.querySelector('[name="quantity"]');
    if (!variantInput) return null;

    var quantity = 1;
    if (quantityInput) {
      quantity = parseInt(quantityInput.value, 10) || 1;
    }

    if (quantity <= 0) return null;

    var productTitle = document.querySelector('.live-variant-product-info h1');
    return {
      variantId: variantInput.value,
      quantity: quantity,
      name: productTitle ? productTitle.textContent.trim() : 'Product',
      packSize: 1,
      inputEl: quantityInput
    };
  }

  function getContextElements(context) {
    switch (context) {
      case 'multi-variant':
        return {
          root: document.querySelector('.custom-variant-product-info'),
          buttons: Array.from(document.querySelectorAll('.custom-variant-product-info .quick-add-to-cart-btn, .custom-variant-product-info .add-to-cart')),
          oosEl: document.querySelector('.custom-variant-product-info #oos-message-wrap'),
          cartLimitEl: document.querySelector('.custom-variant-product-info #cart-limit-msg-wrap'),
          errorEl: document.querySelector('.custom-variant-product-info #oos-message-wrap')
        };
      case 'quick-order':
        return {
          root: document.querySelector('.section-quick-order'),
          buttons: Array.from(document.querySelectorAll('.section-quick-order .add-to-cart')),
          oosEl: document.querySelector('.section-quick-order .quick-order-oos-message-wrap'),
          cartLimitEl: document.querySelector('.section-quick-order .cart-error-mesage'),
          errorEl: document.querySelector('.section-quick-order .quick-order-oos-message-wrap')
        };
      case 'live-product':
        return {
          root: document.querySelector('.live-variant-product-info'),
          buttons: Array.from(document.querySelectorAll('.live-variant-product-info .product-form__add-button[data-action="add-to-cart"]')),
          oosEl: document.querySelector('.live-variant-product-info #oos-message-wrap'),
          cartLimitEl: document.querySelector('.live-variant-product-info #cart-limit-msg-wrap'),
          errorEl: document.querySelector('.live-variant-product-info #oos-message-wrap')
        };
      default:
        return { root: null, buttons: [], errorEl: null };
    }
  }

  function getSelectionsForContext(context) {
    if (context === 'quick-order') return getQuickOrderSelections();
    if (context === 'live-product') {
      var form = document.querySelector('.live-variant-product-info form.product-form');
      var selection = form ? getLiveProductFormSelection(form) : null;
      return selection ? [selection] : [];
    }

    return getMultiVariantSelections(getContextElements('multi-variant').root);
  }

  function shouldWatchSelectionState(context) {
    return context === 'multi-variant' || context === 'quick-order';
  }

  function setOosGuardBlocked(context, blocked) {
    if (!shouldWatchSelectionState(context)) return;
    oosGuardBlockedByContext[context] = !!blocked;
  }

  function isOosGuardBlocked(context) {
    return shouldWatchSelectionState(context) && !!oosGuardBlockedByContext[context];
  }

  function refreshSelectionState(context) {
    var elements = getContextElements(context);
    if (!elements.root) return Promise.resolve();

    var watchButtonState = shouldWatchSelectionState(context);

    if (watchButtonState) {
      hideCartLineLimitError(getCartLimitErrorEl(elements));

      if (!isOosGuardBlocked(context)) {
        setButtonsState(elements.buttons, false);
      }
    }

    var selections = getSelectionsForContext(context);

    if (shouldDebugMultiVariant(context)) {
      debugMultiVariant('refreshSelectionState', {
        watchButtonState: watchButtonState,
        oosGuardBlocked: isOosGuardBlocked(context),
        selections: selections.map(getSelectionDebug)
      }, context);
    }

    if (!selections.length) {
      if (watchButtonState) {
        setOosGuardBlocked(context, false);
        hideOosError(getOosErrorEl(elements));
        setButtonsState(elements.buttons, false);

        if (context === 'multi-variant' || context === 'quick-order') {
          clearOosUiForContext(context);
        }
      }
      return Promise.resolve();
    }

    return fetchCartItems().then(function (cartItems) {
      if (watchButtonState && isOosGuardBlocked(context)) {
        return validateSelections(selections, cartItems, context).then(function (soldOutItems) {
          if (!soldOutItems.length) {
            setOosGuardBlocked(context, false);
            hideOosError(getOosErrorEl(elements));
            setButtonsState(elements.buttons, false);

            if (context === 'multi-variant' || context === 'quick-order') {
              clearOosUiForContext(context);
            }
          } else {
            setButtonsState(elements.buttons, true);
          }

          var soldOutSelections = selections.filter(function (selection) {
            return selectionIsSoldOut(soldOutItems, selection);
          });

          return syncInventoryDom(context, cartItems, soldOutSelections);
        });
      }

      return syncInventoryDom(context, cartItems, []);
    });
  }

  function refreshContext(context) {
    return refreshSelectionState(context);
  }

  function refreshAllContexts() {
    return Promise.all([
      refreshContext('multi-variant'),
      refreshContext('quick-order'),
      refreshContext('live-product')
    ]);
  }

  function scheduleRefresh(source) {
    if (DEBUG_MULTI_VARIANT && (!source || source.indexOf('multi-variant') !== -1 || document.querySelector('.custom-variant-product-info'))) {
      debugMultiVariant('scheduleRefresh', { source: source || 'unknown' }, 'multi-variant');
    }

    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      refreshAllContexts();
    }, 250);
  }

  function guardProceed(proceedFn) {
    return function () {
      window[SKIP_FLAG] = true;

      try {
        proceedFn();
      } finally {
        setTimeout(function () {
          window[SKIP_FLAG] = false;
        }, 0);
      }
    };
  }

  function runGuard(context, proceedFn) {
    var elements = getContextElements(context);
    var cartLineLimitMessage = getCartLineLimitMessage(context);

    if (cartLineLimitMessage) {
      showCartLineLimitError(getCartLimitErrorEl(elements), cartLineLimitMessage);
      if (context !== 'live-product') {
        setButtonsState(elements.buttons, true);
      }
      return Promise.resolve(false);
    }

    if (context !== 'live-product') {
      hideCartLineLimitError(getCartLimitErrorEl(elements));
    }

    var selections = getSelectionsForContext(context);

    if (shouldDebugMultiVariant(context)) {
      debugMultiVariant('runGuard:start', {
        selections: selections.map(getSelectionDebug)
      }, context);
    }

    if (!selections.length) {
      setOosGuardBlocked(context, false);
      hideOosError(getOosErrorEl(elements));
      setButtonsState(elements.buttons, false);

      if (shouldDebugMultiVariant(context)) {
        debugMultiVariant('runGuard: no selections → allow proceed', null, context);
      }

      return Promise.resolve(true);
    }

    return fetchCartItems().then(function (cartItems) {
      if (context === 'live-product') {
        var liveCartLinesEl = document.querySelector('.live-variant-product-info .product-buy-buttons-cart-lines');
        if (liveCartLinesEl) {
          liveCartLinesEl.setAttribute('data-cart-lines', String(cartItems.length));
        }

        var liveSelection = selections[0];
        var liveInCart = !!(liveSelection && cartItems.some(function (item) {
          return String(item.id) === String(liveSelection.variantId);
        }));

        if (!liveInCart && cartItems.length >= 450) {
          showCartLineLimitError(getCartLimitErrorEl(elements), CART_LINE_LIMIT_MESSAGE);
          return false;
        }

        if (cartItems.length < 450) {
          hideCartLineLimitError(getCartLimitErrorEl(elements));
        }
      }

      if (context === 'multi-variant') {
        var cartVariantIds = {};
        cartItems.forEach(function (item) {
          cartVariantIds[String(item.id)] = true;
        });

        var newLineCount = 0;
        selections.forEach(function (selection) {
          if (!cartVariantIds[String(selection.variantId)]) {
            newLineCount += 1;
          }
        });

        if ((cartItems.length + newLineCount) > 450) {
          showCartLineLimitError(getCartLimitErrorEl(elements), CART_LINE_LIMIT_MESSAGE);
          setButtonsState(elements.buttons, true);
          return false;
        }

        hideCartLineLimitError(getCartLimitErrorEl(elements));
      }

      return validateSelections(selections, cartItems, context).then(function (soldOutItems) {
        if (soldOutItems.length > 0) {
          var soldOutSelections = selections.filter(function (selection) {
            return selectionIsSoldOut(soldOutItems, selection);
          });

          if (shouldDebugMultiVariant(context)) {
            debugMultiVariant('runGuard:blocked', {
              soldOutItems: soldOutItems,
              soldOutSelections: soldOutSelections.map(getSelectionDebug)
            }, context);
          }

          return syncInventoryDom(context, cartItems, soldOutSelections).then(function () {
            setOosGuardBlocked(context, true);
            showOosError(getOosErrorEl(elements), soldOutItems);
            setButtonsState(elements.buttons, true);
            return false;
          });
        }

        if (shouldDebugMultiVariant(context)) {
          debugMultiVariant('runGuard:passed → proceed', null, context);
        }

        setOosGuardBlocked(context, false);
        hideOosError(getOosErrorEl(elements));
        setButtonsState(elements.buttons, false);

        if (context === 'multi-variant' || context === 'quick-order') {
          clearOosUiForContext(context);
        }

        proceedFn();
        return true;
      });
    });
  }

  function initListeners() {
    document.addEventListener('input', function (event) {
      if (event.target.matches('.live-variant-product-info [name="quantity"]')) {
        hideOosError(getOosErrorEl(getContextElements('live-product')));
      }

      if (
        event.target.matches('.quantity-input, .quick-order-quantity-input') ||
        event.target.matches('.live-variant-product-info [name="quantity"]')
      ) {
        var source = isMultiVariantElement(event.target)
          ? 'input:multi-variant'
          : 'input:other';
        scheduleRefresh(source);
      }
    });

    document.addEventListener('click', function (event) {
      if (event.target.closest('.quantity-update, .quick-order-quantity-update')) {
        var btn = event.target.closest('.quantity-update, .quick-order-quantity-update');
        var source = isMultiVariantElement(btn)
          ? 'click:quantity-btn:multi-variant'
          : 'click:quantity-btn:other';
        scheduleRefresh(source);
      }
    });

    document.addEventListener('initUpdateQuantity', function () {
      syncMultiVariantStorefrontInventory().finally(function () {
        scheduleRefresh('event:initUpdateQuantity');
      });
    });
    document.addEventListener('variant:changed', function () {
      hideOosError(getOosErrorEl(getContextElements('live-product')));
      scheduleRefresh('event:variant:changed');
    });
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) {
        if (DEBUG_MULTI_VARIANT) {
          debugMultiVariant('visibilitychange → refreshAllContexts', null, 'multi-variant');
        }
        refreshAllContexts();
      }
    });

    document.addEventListener(
      'click',
      function (event) {
        if (window[SKIP_FLAG]) return;

        var liveButton = event.target.closest('.live-variant-product-info .product-form__add-button[data-action="add-to-cart"]');
        if (liveButton && !liveButton.disabled) {
          event.preventDefault();
          event.stopPropagation();
          if (typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation();
          }

          runGuard('live-product', guardProceed(function () {
            liveButton.click();
          }));

          return;
        }

        var quickOrderButton = event.target.closest('.section-quick-order .add-to-cart');
        if (quickOrderButton) {
          event.preventDefault();
          event.stopPropagation();
          if (typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation();
          }

          runGuard('quick-order', guardProceed(function () {
            quickOrderButton.click();
          }));
        }
      },
      true
    );
  }

  window.TTVariantAvailability = {
    buildErrorMessage: buildErrorMessage,
    fetchCartItems: fetchCartItems,
    validateSelections: validateSelections,
    showError: showError,
    showOosError: showOosError,
    showCartLineLimitError: showCartLineLimitError,
    hideCartLineLimitError: hideCartLineLimitError,
    hideError: hideError,
    hideOosError: hideOosError,
    setButtonsState: setButtonsState,
    getMultiVariantSelections: getMultiVariantSelections,
    getQuickOrderSelections: getQuickOrderSelections,
    refreshAllContexts: refreshAllContexts,
    refreshSelectionState: refreshSelectionState,
    scheduleRefresh: scheduleRefresh,
    runGuard: runGuard,
    guardProceed: guardProceed,
    debugMultiVariant: debugMultiVariant,
    logMultiVariantInputsSnapshot: logMultiVariantInputsSnapshot,
    syncMultiVariantStorefrontInventory: syncMultiVariantStorefrontInventory,
    syncMultiVariantOosUi: syncMultiVariantOosUi,
    syncQuickOrderOosUi: syncQuickOrderOosUi,
    isOosGuardBlocked: isOosGuardBlocked,
    DEBUG_MULTI_VARIANT: DEBUG_MULTI_VARIANT
  };

  function bootMultiVariantAvailability() {
    initListeners();

    if (DEBUG_MULTI_VARIANT) {
      debugMultiVariant('init', {
        tip: 'Disable logs: localStorage.setItem("TT_DEBUG_MULTI_VARIANT","0") or window.TT_DEBUG_MULTI_VARIANT=false'
      }, 'multi-variant');
      logMultiVariantInputsSnapshot('init: SSR snapshot (liquid, may be stale)');
    }

    syncMultiVariantStorefrontInventory().finally(function () {
      refreshAllContexts();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootMultiVariantAvailability);
  } else {
    bootMultiVariantAvailability();
  }
})();
